/**
 * Case Studies Data Registry
 * Central registry for all case studies
 */

export interface CaseStudy {
  title: string;
  slug: string;
  description: string;
  category: 'Cybersecurity' | 'Cloud Solutions' | 'Compliance' | 'Infrastructure';
  industry?: string;
  featuredImage?: string;
  filename?: string; // WordPress backup filename
  featured?: boolean;
  results?: {
    metric: string;
    value: string;
  }[];
  tags?: string[];
}

export const caseStudies: CaseStudy[] = [
  {
    title: "How to Strengthen Cybersecurity for a Top 10 Domestic US Airline",
    slug: "how-to-strengthen-cybersecurity-for-a-top-10-domestic-us-airline",
    description: "Discover how a top 10 US airline neutralized a foreign cyber threat, secured critical systems, and built a resilient cybersecurity framework",
    category: "Cybersecurity",
    industry: "Transportation",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/ee6051f6-acd4-4ecb-547a-c95f809eae00/public",
    filename: "case-study-how-to-strengthen-cybersecurity-for-a-top-10-domestic-us-airline_c1b72e3b",
    featured: true,
    tags: ["Threat Detection", "Incident Response", "Aviation Security"]
  },
  {
    title: "Modernizing a 3-Tier Web Application with GKE",
    slug: "modernizing-a-3-tier-web-application-for-cost-savings-and-resilience",
    description: "Modernize a 3-tier web app with GKE: cut compute costs by 90% and boost scalability with Linux migration and custom scaling.",
    category: "Cloud Solutions",
    industry: "Technology",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/8d81efc9-2cd9-482f-da9a-a7eb4cebf300/public",
    filename: "case-study-modernizing-a-3-tier-web-application-for-cost-savings-and-resilience_332e5533",
    featured: true,
    results: [
      { metric: "Cost Reduction", value: "90%" },
      { metric: "Scalability", value: "10x improvement" }
    ],
    tags: ["Google Cloud", "Kubernetes", "Cost Optimization"]
  },
  {
    title: "Autoscaling for Black Friday Traffic Surges",
    slug: "autoscaling-for-black-friday-traffic-surges",
    description: "How autoscaling helped an eCommerce client cut costs by 85% and handle Black Friday traffic spikes seamlessly.",
    category: "Cloud Solutions",
    industry: "E-commerce",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/71003212-771f-4476-9b24-b645274f5200/public",
    filename: "case-study-autoscaling-for-black-friday-traffic-surges_1fe269b9",
    featured: true,
    results: [
      { metric: "Cost Savings", value: "85%" },
      { metric: "Uptime", value: "100%" }
    ],
    tags: ["Autoscaling", "E-commerce", "Load Balancing"]
  },
  {
    title: "Ensuring Consistent Security and Compliance Across a Diverse Server Infrastructure",
    slug: "ensuring-consistent-security-and-compliance",
    description: "A large enterprise in the transportation industry with approximately 3,000 employees and around 700 servers faced significant challenges in managing its growing IT infrastructure.",
    category: "Cybersecurity",
    industry: "Transportation",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/723b3cae-308a-4a00-2a22-7f765f475100/public",
    filename: "case-study_eb14889c",
    featured: true,
    tags: ["Compliance", "CrowdStrike", "Infrastructure Security"]
  },
  {
    title: "Cutting IT Costs by 38% with Cloud Migration",
    slug: "cloud-migration-and-file-server-cost-optimization",
    description: "Learn how cloud migration cut IT costs by 38% with NetApp Cloud Volumes, boosting scalability and efficiency.",
    category: "Cloud Solutions",
    industry: "Enterprise",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/699bc19f-7f6c-401e-b596-2b3da22bb800/public",
    filename: "case-study-cloud-migration-and-file-server-cost-optimization_2166852a",
    featured: true,
    results: [
      { metric: "Cost Reduction", value: "38%" },
      { metric: "Storage Efficiency", value: "Improved" }
    ],
    tags: ["Cloud Migration", "NetApp", "Cost Optimization"]
  },
  {
    title: "How to do Cybersecurity Across a Distributed Organization",
    slug: "how-to-do-cybersecurity-across-a-distributed-organization",
    description: "Learn how a healthcare group improved cybersecurity with a vCISO, standardized controls, and advanced threat protection.",
    category: "Cybersecurity",
    industry: "Healthcare",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/748a4641-6eaf-4ad0-02e5-dbe4d26fd300/public",
    filename: "case-study-how-to-do-cybersecurity-across-a-distributed-organization_43553e2e",
    featured: true,
    tags: ["vCISO", "Healthcare", "Distributed Security"]
  },
  {
    title: "Eliminating a $50,000 Annual Expense for a Legacy Accounting Database",
    slug: "eliminating-50000-annual-expense-legacy-accounting-database",
    description: "How migrating a legacy MS SQL database to Google BigQuery reduced costs by 99.8% while maintaining seamless access to historical financial data.",
    category: "Cloud Solutions",
    industry: "Enterprise",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/14351477-08ae-45ed-f521-fe0c67417800/public",
    filename: "untitled-2_e27cb71f",
    featured: true,
    results: [
      { metric: "Cost Reduction", value: "99.8%" },
      { metric: "Migration Time", value: "1 Week" }
    ],
    tags: ["BigQuery", "Cost Optimization", "Legacy Migration", "Data Warehouse"]
  },
  {
    title: "Building an AI-Powered SaaS Platform on Cloudflare's Edge",
    slug: "building-ai-saas-platform-on-cloudflare-edge",
    description: "How we designed and built a full-stack AI resume platform entirely on Cloudflare's edge infrastructure — leveraging Workers, D1, R2, Vectorize, AI Gateway, and Browser Rendering to deliver sub-100ms global latency at a fraction of traditional cloud costs.",
    category: "Cloud Solutions",
    industry: "SaaS / AI",
    featuredImage: "/images/case-studies/building-ai-saas-platform-on-cloudflare-edge.png",
    filename: "case-study-building-ai-saas-platform-on-cloudflare-edge_cf01",
    featured: true,
    results: [
      { metric: "Cloudflare Products", value: "10+" },
      { metric: "AI Cost per User", value: "$0.15/mo" },
      { metric: "Cold Starts", value: "Zero" },
      { metric: "Origin Servers", value: "None" }
    ],
    tags: ["Cloudflare Workers", "AI Gateway", "Vectorize", "D1", "R2", "Edge Computing", "RAG", "SaaS"]
  },
  {
    title: "Building a Developer-First Email Platform on Cloudflare's Edge",
    slug: "building-developer-first-email-platform-on-cloudflare-edge",
    description: "Tired of wiring up the same email infrastructure for every project, we built PushMail — an API-first email platform on Cloudflare Workers that handles transactional, sequence, and campaign email through one REST API, one account, and pay-per-send pricing.",
    category: "Cloud Solutions",
    industry: "SaaS / Developer Tools",
    featuredImage: "/images/case-studies/building-developer-first-email-platform-on-cloudflare-edge.png",
    filename: "case-study-building-developer-first-email-platform-on-cloudflare-edge_pm01",
    featured: true,
    results: [
      { metric: "Cloudflare Products", value: "10+" },
      { metric: "Per-Email Cost", value: "$0.003" },
      { metric: "Cold Starts", value: "Zero" },
      { metric: "Origin Servers", value: "None" }
    ],
    tags: ["Cloudflare Workers", "D1", "R2", "KV", "Queues", "Email Infrastructure", "SaaS", "Edge Computing", "SendGrid"]
  },
  {
    title: "Stopping a Phishing Attack in Its Tracks with Microsoft Sentinel",
    slug: "stopping-a-phishing-attack-in-its-tracks-with-microsoft-sentinel",
    description: "How a payroll company detected and contained a phishing attack within one hour using Microsoft Sentinel — maintaining client trust and regulatory compliance.",
    category: "Cybersecurity",
    industry: "Payroll & Workforce Management",
    featuredImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/6913e527-2a6f-4e25-d802-46c42f6d0500/public",
    filename: "case-study-stopping-a-phishing-attack-in-its-tracks-with-microsoft-sentinel_sentinel01",
    featured: true,
    results: [
      { metric: "Response Time", value: "< 1 Hour" },
      { metric: "Data Breach", value: "Prevented" }
    ],
    tags: ["Microsoft Sentinel", "SIEM", "Incident Response", "Phishing Defense", "Entra ID"]
  },
  {
    title: "Building an Incident Management Platform on Cloudflare's Edge",
    slug: "building-incident-management-platform-on-cloudflare-edge",
    description: "How we designed and built Alert24 — an all-in-one monitoring, on-call scheduling, incident management, and status page platform — entirely on Cloudflare's edge infrastructure, replacing a $200+/month multi-vendor stack with a single $24/month platform.",
    category: "Cloud Solutions",
    industry: "SaaS / DevOps",
    featuredImage: "/images/case-studies/building-incident-management-platform-on-cloudflare-edge.png",
    filename: "case-study-building-incident-management-platform-on-cloudflare-edge_a24",
    featured: true,
    results: [
      { metric: "Tools Replaced", value: "3" },
      { metric: "Starting Price", value: "$24/mo" },
      { metric: "Cold Starts", value: "Zero" },
      { metric: "Origin Servers", value: "None" }
    ],
    tags: ["Cloudflare Workers", "D1", "Incident Management", "Monitoring", "Status Pages", "Edge Computing", "SaaS"]
  },
  {
    title: "Building a Self-Hosted Webmail Client on Cloudflare's Edge",
    slug: "building-self-hosted-webmail-client-on-cloudflare-edge",
    description: "How we built orange-inbox — an open-source, Gmail-style webmail client that runs entirely on Cloudflare — giving Email Routing users a real inbox with no mail server, no origin infrastructure, and mail that never leaves their own account.",
    category: "Cloud Solutions",
    industry: "Open Source / Email",
    featuredImage: "/images/case-studies/building-self-hosted-webmail-client-on-cloudflare-edge.png",
    filename: "case-study-building-self-hosted-webmail-client-on-cloudflare-edge_oi01",
    featured: true,
    results: [
      { metric: "Cloudflare Products", value: "10+" },
      { metric: "Mail Servers", value: "None" },
      { metric: "Third-Party Vendors", value: "None" },
      { metric: "Cold Starts", value: "Zero" }
    ],
    tags: ["Cloudflare Workers", "D1", "R2", "KV", "Email Routing", "Cloudflare Access", "OpenNext", "Webmail", "Edge Computing", "Open Source"]
  }
];

// Helper functions
export function getAllCaseStudies(): CaseStudy[] {
  return caseStudies;
}

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return caseStudies.find(caseStudy => caseStudy.slug === slug);
}

export function getCaseStudiesByCategory(category: string): CaseStudy[] {
  return caseStudies.filter(caseStudy => caseStudy.category === category);
}

export function getFeaturedCaseStudies(limit: number = 3): CaseStudy[] {
  return caseStudies
    .filter(caseStudy => caseStudy.featured)
    .slice(0, limit);
}

export function getCaseStudiesByIndustry(industry: string): CaseStudy[] {
  return caseStudies.filter(caseStudy => caseStudy.industry === industry);
}

export function getRelatedCaseStudies(currentSlug: string, limit: number = 3): CaseStudy[] {
  const currentCaseStudy = getCaseStudyBySlug(currentSlug);
  if (!currentCaseStudy) return [];

  // Get case studies from same category or industry
  const related = caseStudies
    .filter(cs =>
      cs.slug !== currentSlug &&
      (cs.category === currentCaseStudy.category || cs.industry === currentCaseStudy.industry)
    )
    .slice(0, limit);

  // If not enough, fill with other featured case studies
  if (related.length < limit) {
    const additional = caseStudies
      .filter(cs =>
        cs.slug !== currentSlug &&
        !related.find(r => r.slug === cs.slug) &&
        cs.featured
      )
      .slice(0, limit - related.length);

    return [...related, ...additional];
  }

  return related;
}
